// src/features/tickets/services/TicketAttachmentZipService.ts
//
// Sammel-Download aller regulären Anhänge einer Ticket-Nachricht als ZIP.
//
// Ablauf:
//   1. Vollständigkeitsprüfung: Die Zahl der angezeigten Anhänge wird gegen
//      eine Count-Query auf ticket_attachments abgeglichen. Weicht sie ab,
//      wird abgebrochen (Schutz gegen gekappte oder veraltete Listen).
//   2. Deduplizierung nach storage_path: Mehrere Zeilen auf demselben Pfad
//      verweisen physisch auf dieselbe Datei. Sie kommt nur einmal ins ZIP.
//   3. Download direkt aus dem Bucket über die authentifizierte Session
//      (Storage-Policy authenticated_select_ticket_attachments), begrenzt
//      parallel.
//   4. Alles-oder-nichts: Scheitert eine einzige Datei, entsteht kein ZIP.
//   5. ZIP ohne erneute Komprimierung (PDF/XLSX sind bereits komprimiert),
//      Download im Browser über eine temporäre Object-URL.
//
// Inline-Bilder (is_inline) sind nie Teil des ZIPs.

import JSZip from 'jszip';
import { supabase } from '../../../shared/services/supabase';
import { TicketAttachment, TicketOperationResult } from '../types';

const BUCKET = 'ticket-attachments';
const PARALLEL_DOWNLOADS = 6;
const OBJECT_URL_LIFETIME_MS = 10000;

export type ZipProgressPhase = 'download' | 'zip';

export interface ZipProgress {
  phase: ZipProgressPhase;
  done: number;
  total: number;
}

export interface MessageAttachmentZipInput {
  ticketNumber: string;
  messageId: string;
  messageCreatedAt: string;
  attachments: TicketAttachment[];
  onProgress?: (progress: ZipProgress) => void;
}

class TicketAttachmentZipService {
  /**
   * Lädt alle regulären Anhänge einer Nachricht, packt sie in ein ZIP und
   * startet den Browser-Download. Liefert success=false mit Fehlertext, wenn
   * die Liste nicht vollständig ist oder eine Datei nicht geladen werden kann.
   */
  async downloadMessageAttachments(input: MessageAttachmentZipInput): Promise<TicketOperationResult> {
    try {
      const regular = input.attachments.filter(att => !att.is_inline);
      if (regular.length === 0) {
        return { success: false, error: 'Diese Nachricht hat keine Anhänge.' };
      }

      // 1. Vollständigkeitsprüfung gegen den gespeicherten Stand
      const { count, error: countError } = await supabase
        .from('ticket_attachments')
        .select('id', { count: 'exact', head: true })
        .eq('message_id', input.messageId)
        .eq('is_inline', false);

      if (countError) {
        console.error('[TicketAttachmentZipService] Count-Error:', countError);
        return { success: false, error: 'Die Anhangsliste konnte nicht geprüft werden. Es wurde kein ZIP erstellt.' };
      }
      if (count !== regular.length) {
        return {
          success: false,
          error: `Die angezeigte Anhangsliste weicht vom gespeicherten Stand ab (angezeigt ${regular.length}, gespeichert ${count ?? 0}). Bitte die Seite neu laden und erneut versuchen.`
        };
      }

      // 2. Deduplizierung nach storage_path
      const unique: TicketAttachment[] = [];
      const seenPaths = new Set<string>();
      for (const att of regular) {
        if (seenPaths.has(att.storage_path)) continue;
        seenPaths.add(att.storage_path);
        unique.push(att);
      }

      // 3. Download mit begrenzter Parallelität
      const blobs: Array<Blob | null> = new Array<Blob | null>(unique.length).fill(null);
      const state: { nextIndex: number; done: number; failed: TicketAttachment | null } = {
        nextIndex: 0,
        done: 0,
        failed: null
      };

      input.onProgress?.({ phase: 'download', done: 0, total: unique.length });

      const worker = async (): Promise<void> => {
        while (state.failed === null && state.nextIndex < unique.length) {
          const index = state.nextIndex;
          state.nextIndex += 1;
          const att = unique[index];
          try {
            const { data, error } = await supabase.storage.from(BUCKET).download(att.storage_path);
            if (error || !data) {
              console.error('[TicketAttachmentZipService] Download-Error:', att.storage_path, error);
              if (state.failed === null) state.failed = att;
              return;
            }
            blobs[index] = data;
            state.done += 1;
            input.onProgress?.({ phase: 'download', done: state.done, total: unique.length });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error('[TicketAttachmentZipService] Download-Exception:', att.storage_path, msg);
            if (state.failed === null) state.failed = att;
            return;
          }
        }
      };

      const workerCount = Math.min(PARALLEL_DOWNLOADS, unique.length);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      // 4. Alles-oder-nichts
      if (state.failed !== null) {
        return {
          success: false,
          error: `Der Anhang „${state.failed.filename}“ konnte nicht geladen werden. Es wurde kein ZIP erstellt.`
        };
      }
      if (blobs.some(blob => blob === null)) {
        return { success: false, error: 'Nicht alle Anhänge konnten geladen werden. Es wurde kein ZIP erstellt.' };
      }

      // 5. ZIP bauen und herunterladen
      input.onProgress?.({ phase: 'zip', done: unique.length, total: unique.length });

      const zip = new JSZip();
      const usedNames = new Set<string>();
      unique.forEach((att, index) => {
        const blob = blobs[index];
        if (blob) {
          zip.file(buildUniqueEntryName(att.filename, usedNames), blob, { binary: true });
        }
      });

      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
      triggerBrowserDownload(zipBlob, buildZipFileName(input.ticketNumber, input.messageCreatedAt));

      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[TicketAttachmentZipService] Unexpected error:', msg);
      return { success: false, error: 'Das ZIP konnte nicht erstellt werden.' };
    }
  }
}

// Singleton-Export
export const ticketAttachmentZipService = new TicketAttachmentZipService();

// =============================================================================
// Helper
// =============================================================================

/**
 * Macht aus dem Anzeigenamen einen gültigen ZIP-Eintrag. Schrägstriche würden
 * im ZIP als Ordner interpretiert und werden daher durch "_" ersetzt.
 */
function sanitizeEntryName(filename: string): string {
  const cleaned = (filename || '').replace(/[/\\]/g, '_').trim();
  return cleaned || 'Anhang';
}

/**
 * Liefert einen im ZIP eindeutigen Dateinamen. Kollisionen werden ohne
 * Beachtung der Groß-/Kleinschreibung erkannt (macOS und Windows entpacken
 * case-insensitiv) und mit " (2)", " (3)" vor der Endung aufgelöst.
 */
function buildUniqueEntryName(filename: string, usedNames: Set<string>): string {
  const base = sanitizeEntryName(filename);
  const dotIndex = base.lastIndexOf('.');
  const stem = dotIndex > 0 ? base.slice(0, dotIndex) : base;
  const extension = dotIndex > 0 ? base.slice(dotIndex) : '';

  let candidate = base;
  let counter = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    candidate = `${stem} (${counter})${extension}`;
    counter += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

/**
 * ZIP-Dateiname aus Ticketnummer und Zeitpunkt der Nachricht in Berliner Zeit,
 * z. B. T-2026-14426_2026-09-29_1058_Anhaenge.zip. Bewusst ohne Umlaute.
 */
function buildZipFileName(ticketNumber: string, messageCreatedAt: string): string {
  const date = new Date(messageCreatedAt);
  if (Number.isNaN(date.getTime())) {
    return `${ticketNumber}_Anhaenge.zip`;
  }
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${ticketNumber}_${m.year}-${m.month}-${m.day}_${m.hour}${m.minute}_Anhaenge.zip`;
}

/** Startet den Download eines Blobs über eine temporäre Object-URL. */
function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
}
